<template>
  <div class="row">
    <div class="col-md-3 col-lg-2">
      <MemberProfilesNav :tabs="setting_tabs" />
    </div>

    <div class="col-md-8 col-sm-9 col-lg-10 border-left pl-lg-30 setting_wrapper settings_panel">
      <div
        v-if="!plan_allowed && !planBannerDismissed"
        class="alert alert-disabled"
        style="position: relative"
        data-testid="api-tokens-plan-banner"
      >
        <i
          class="hi hi-close hi-10 alert-close"
          data-testid="api-tokens-plan-banner-close"
          @click="planBannerDismissed = true"
        />
        <div class="alert-inner-wrapper d-flex align-items-center">
          <div class="alert-body d-flex align-items-center">
            <i class="hi hi-16 hi-lock ml-5 mr-15 text-blue" />
            <span>A Team or Enterprise plan is required to use API tokens. Upgrade to try it out today.</span>
          </div>
          <div class="alert-action d-flex align-items-center">
            <a
              v-if="upgrade_url"
              :href="upgrade_url"
              class="btn btn-sm btn-primary mr-15"
            >Upgrade plan</a>
          </div>
        </div>
      </div>

      <div :class="{ 'section-disabled': !plan_allowed }">
        <div class="d-sm-flex justify-content-between align-items-start mb-20">
          <div class="mb-10">
            <h5
              class="setting-title"
              data-testid="api-tokens-page-heading"
            >API access tokens</h5>
            <p
              class="text-muted"
              style="max-width: 64ch"
            >
              Organization access tokens authenticate the Hubstaff API. The tokens have the same access
              level as the member they're assigned to.
              <a
                v-if="developer_portal_url"
                :href="developer_portal_url"
                target="_blank"
                rel="noopener"
              >Learn more in the developer portal</a>
              <i
                v-tooltip="{ html: true, placement: 'bottom', title: accessHelp }"
                class="hi hi-info-filled text-lighter ml-5"
                data-testid="api-tokens-help"
              />
            </p>
          </div>

          <AppButton
            type="primary"
            :disabled="!plan_allowed"
            data-testid="create-api-token-button"
            @click="showCreate = true"
          >
            Create token
          </AppButton>
        </div>

        <ApiTokensTable
          :tokens="tokens"
          @revoke="revokeToken"
          @reassign="reassignTarget = $event"
        />
      </div>
    </div>

    <ApiTokenFormDialog
      :show="showCreate"
      :organization-id="organization.id"
      :expiry-options="expiry_options"
      @close="showCreate = false"
    />

    <ApiTokenFormDialog
      :show="Boolean(reassignTarget)"
      :organization-id="organization.id"
      :token="reassignTarget"
      @close="reassignTarget = null"
    />

    <ApiTokenSecretDialog
      :show="showSecret"
      :secret="revealedSecret || ''"
      @close="onSecretClose"
    />
  </div>
</template>

<script>
import { InertiaFlashWatcher, InertiaLoadingWatcher } from '~/utils/inertia';
import MemberProfilesNav from '~/components/settings/MemberProfilesNav.vue';
import ApiTokensTable from '~/components/api_tokens/ApiTokensTable.vue';
import ApiTokenFormDialog from '~/components/api_tokens/ApiTokenFormDialog.vue';
import ApiTokenSecretDialog from '~/components/api_tokens/ApiTokenSecretDialog.vue';

export default {
  name: 'ApiTokens',
  components: { MemberProfilesNav, ApiTokensTable, ApiTokenFormDialog, ApiTokenSecretDialog },
  mixins: [InertiaFlashWatcher, InertiaLoadingWatcher],
  props: {
    organization: { type: Object, required: true },
    setting_tabs: { type: Array, required: true },
    tokens: { type: Array, required: true },
    expiry_options: { type: Array, required: true },
    developer_portal_url: { type: String, default: '' },
    plan_allowed: { type: Boolean, default: true },
    upgrade_url: { type: String, default: '' },
    created_secret: { type: String, default: null },
  },
  data() {
    return { showCreate: false, showSecret: false, revealedSecret: null, reassignTarget: null, planBannerDismissed: false };
  },
  computed: {
    accessHelp() {
      return [
        "If the token's assigned member has their access or role changed, the token's access changes too.",
        "If the member leaves the organization the token stops working until it's reassigned.",
        "A token's secret is shown only once, right after you create it — store it somewhere safe.",
      ].join('<br><br>');
    },
  },
  watch: {
    created_secret: {
      immediate: true,
      handler(secret) {
        if (!secret) {return;}

        // Reveal once, then strip from history state so it can't be recovered later.
        this.revealedSecret = secret;
        this.showCreate = false;
        this.showSecret = true;
        this.scrubSecretFromHistory();
      },
    },
  },
  methods: {
    onSecretClose() {
      this.showSecret = false;
      this.revealedSecret = null;
    },
    scrubSecretFromHistory() {
      // Clear both $page.props and history.state so back/forward can't re-reveal the secret.
      try {
        if (this.$page && this.$page.props && 'created_secret' in this.$page.props) {
          this.$page.props.created_secret = null;
        }

        const state = window.history.state;

        if (state && state.props && 'created_secret' in state.props) {
          state.props.created_secret = null;
          window.history.replaceState(state, '', window.location.href);
        }
      } catch (e) {
        // best effort — never block the dialog on history access
      }
    },
    revokeToken(token) {
      // An expired or dormant token is already not working, so don't warn about interrupting integrations.
      const working = !token.expired && !token.needs_reassignment;
      const warning = working
        ? `Revoke "${token.name}"? Integrations using it will stop working immediately.`
        : `Revoke "${token.name}"? This permanently removes it and can't be undone.`;

      if (!window.confirm(warning)) {return;}

      this.$inertia.delete(
        `/organizations/${this.organization.id}/settings/api_tokens/${token.id}`,
        { preserveScroll: true },
      );
    },
  },
};
</script>
